import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Minus, Plus, ShoppingBag, ChevronLeft, Crown, Heart, Flame } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../contexts/CurrencyContext';
import LazyImage from '../components/LazyImage';
import RecentlyViewed, { addToRecentlyViewed } from '../components/RecentlyViewed';
import FrequentlyBought from '../components/FrequentlyBought';
import CustomersAlsoBought from '../components/CustomersAlsoBought';
import ProductSchema from '../components/ProductSchema';
import ProductReviews from '../components/ProductReviews';
import Breadcrumbs from '../components/Breadcrumbs';
import BreadcrumbSchema from '../components/BreadcrumbSchema';
import SEOHead from '../components/SEOHead';
import FragrancePyramid from '../components/FragrancePyramid';
import PerfectFor from '../components/PerfectFor';
import LiveProductViewers from '../components/LiveProductViewers';
import ShareButtons from '../components/ShareButtons';
import CategorySections from '../components/CategorySections';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { trackProductView, trackAddToCart, trackAddToWishlist } from '../utils/analytics';
import { playWishlist } from '../utils/sound';

const API = `${window.location.origin}/api`;

export default function ProductPage() {
  const { id } = useParams();
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const { formatPrice } = useCurrency();
  // Hydrate from sessionStorage cache so navigating back to a previously-viewed
  // product feels instant (no skeleton flash, no API roundtrip wait).
  const cachedProduct = (() => {
    try {
      const raw = sessionStorage.getItem(`jsf_product_${id}`);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [product, setProduct] = useState(cachedProduct);
  const [loading, setLoading] = useState(!cachedProduct);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [wishlisted, setWishlisted] = useState(false);
  const [layeringCombos, setLayeringCombos] = useState(null);
  const [layeringLoading, setLayeringLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products/${id}`).then(res => {
      setProduct(res.data);
      try { sessionStorage.setItem(`jsf_product_${id}`, JSON.stringify(res.data)); } catch {}
      addToRecentlyViewed(res.data);
      trackProductView(res.data.id, res.data.name, res.data.base_price || 0);
      if (res.data.enable_variants && res.data.variants?.length > 0) {
        const sizes = [...new Set(res.data.variants.map(v => v.size))];
        const types = [...new Set(res.data.variants.map(v => v.type))];
        setSelectedSize(sizes[0] || null);
        setSelectedType(types[0] || null);
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [id]);

  // Check wishlist status
  useEffect(() => {
    if (!isAuthenticated || !id) return;
    const token = localStorage.getItem('auth_token');
    axios.get(`${API}/customer/wishlist`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { setWishlisted(res.data.some(p => p.id === id)); })
      .catch(() => {});
  }, [isAuthenticated, id]);

  const toggleWishlist = async () => {
    if (!isAuthenticated) { toast.error('Login to save to wishlist'); return; }
    const token = localStorage.getItem('auth_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (wishlisted) {
        await axios.delete(`${API}/customer/wishlist/${id}`, { headers });
        setWishlisted(false);
        toast.success('Removed from wishlist');
      } else {
        await axios.post(`${API}/customer/wishlist`, { product_id: id }, { headers });
        setWishlisted(true);
        playWishlist();
        trackAddToWishlist(id, product?.name, product?.base_price || 0);
        toast.success('Added to wishlist');
      }
    } catch { toast.error('Failed to update wishlist'); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-white/40">Loading...</div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/40 text-lg mb-4">Product not found</p>
          <Link to="/shop" className="text-[#D4AF37] text-sm uppercase tracking-widest">Back to Shop</Link>
        </div>
      </div>
    );
  }

  const hasVariants = product.enable_variants && product.variants?.length > 0;
  const sizes = hasVariants ? [...new Set(product.variants.map(v => v.size))] : [];
  const types = hasVariants ? [...new Set(product.variants.map(v => v.type))] : [];

  const selectedVariant = hasVariants
    ? product.variants.find(v => v.size === selectedSize && v.type === selectedType)
    : null;

  const displayPrice = hasVariants && selectedVariant ? selectedVariant.price : product.base_price;
  const inStock = hasVariants
    ? (selectedVariant ? selectedVariant.stock > 0 : false)
    : product.stock > 0;
  const canAdd = hasVariants ? !!selectedVariant && inStock : inStock;

  const handleAddToCart = () => {
    if (!canAdd) return;
    addToCart(product, quantity, selectedVariant ? { size: selectedSize, type: selectedType, price: selectedVariant.price } : null);
    trackAddToCart(product.id, product.name, selectedVariant?.price || product.base_price, quantity);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <div data-testid="product-page" className="min-h-screen">
      <SEOHead
        title={product.name}
        description={(product.description || `${product.name} — EDP bottled at 30% oil concentration (Extrait-level) by JS Fragrance.`).slice(0, 200)}
        image={product.image_url ? (product.image_url.startsWith('http') ? product.image_url : `https://jsfragrances.com${product.image_url}`) : undefined}
        url={`https://jsfragrances.com/product/${product.id}`}
        type="product"
      />
      <ProductSchema product={product} />
      <BreadcrumbSchema crumbs={[
        { name: 'Home', path: '/' },
        { name: 'Shop', path: '/shop' },
        { name: product.name, path: `/product/${product.id}` },
      ]} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Link to="/shop" className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-shop">
          <ChevronLeft className="w-4 h-4" /> Back to Shop
        </Link>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
          {/* Image */}
          <div className="bg-[#141414] border border-white/5 aspect-[3/4] overflow-hidden relative" data-testid="product-image">
            <LazyImage
              src={product.image_url}
              alt={product.name}
              className="w-full h-full"
            />
            {product.is_signature && (
              <div className="absolute top-0 left-0 bg-[#D4AF37] text-black px-3 py-1.5 flex items-center gap-1.5" data-testid="product-signature-badge">
                <Crown className="w-3.5 h-3.5" />
                <span className="text-[10px] uppercase tracking-[0.15em] font-sans font-bold">JS Original</span>
              </div>
            )}
            {(() => {
              const stockNum = (() => {
                if (selectedVariant) return selectedVariant.stock;
                if (product.variants?.length) {
                  return product.variants.reduce((s, v) => s + (v.stock || 0), 0);
                }
                return product.stock || 0;
              })();
              if (stockNum > 0 && stockNum < 5) {
                return (
                  <div className="absolute top-0 right-0 bg-[#F59E0B] text-black px-3 py-1.5" data-testid="stock-urgency-badge">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-sans font-bold">Only {stockNum} left</span>
                  </div>
                );
              }
              if (stockNum === 0) {
                return (
                  <div className="absolute top-0 right-0 bg-red-500/90 text-white px-3 py-1.5" data-testid="stock-out-badge">
                    <span className="text-[10px] uppercase tracking-[0.15em] font-sans font-bold">Out of Stock</span>
                  </div>
                );
              }
              return null;
            })()}
          </div>

          {/* Details */}
          <div className="flex flex-col justify-center">
            {/* Breadcrumbs (#28) */}
            <div className="mb-3">
              <Breadcrumbs items={[
                { label: 'Home', to: '/' },
                { label: 'Shop', to: '/shop' },
                { label: product.name },
              ]} />
            </div>
            <div className="flex items-center gap-2 mb-2">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-sans">
                {product.is_signature ? 'JS Original' : product.gender}
              </p>
              {product.is_signature && (
                <span className="text-xs uppercase tracking-[0.15em] text-white/30 font-sans">&middot; {product.gender}</span>
              )}
              <LiveProductViewers productId={product.id} />
            </div>
            <h1 className="hero-headline text-3xl sm:text-4xl tracking-tight font-medium text-white mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {product.name}
            </h1>
            <p className="text-white/50 text-base mb-4 font-light">{product.description}</p>

            {product.is_signature && (
              <div className="mb-5">
                <span data-testid="crafted-by-jsf-badge" className="crafted-pill inline-flex items-center gap-2 pl-2.5 pr-3.5 py-1.5 rounded-full border border-[#D4AF37]/40 bg-gradient-to-r from-[#D4AF37]/[0.08] via-[#D4AF37]/[0.04] to-transparent cursor-default">
                  <span className="flex items-center justify-center w-5 h-5 rounded-full bg-[#D4AF37] text-black">
                    <Crown className="w-3 h-3" strokeWidth={2.5} />
                  </span>
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-sans font-semibold">
                    Crafted by JS Fragrance
                  </span>
                </span>
              </div>
            )}

            {/* Perfect for chips (#11) */}
            <div className="mb-6">
              <PerfectFor product={product} />
            </div>

            {/* Price */}
            <p className="text-2xl text-[#D4AF37] mb-6 font-sans font-medium" data-testid="product-price">
              {formatPrice(displayPrice)}
            </p>

            {/* Variants or fixed label */}
            {hasVariants ? (
              <div className="space-y-5 mb-6">
                {/* Size selector */}
                {sizes.length > 0 && (
                  <div data-testid="size-selector">
                    <label className="text-xs uppercase tracking-[0.15em] text-white/50 mb-2 block font-sans">Size</label>
                    <div className="flex flex-wrap gap-2">
                      {sizes.map(size => (
                        <button
                          key={size}
                          onClick={() => setSelectedSize(size)}
                          data-testid={`size-option-${size}`}
                          className={`px-4 py-2 text-sm font-sans transition-all ${
                            selectedSize === size
                              ? 'bg-[#D4AF37] text-black font-medium'
                              : 'border border-white/20 text-white/60 hover:border-white/40'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Type selector */}
                {types.length > 0 && (
                  <div data-testid="type-selector">
                    <label className="text-xs uppercase tracking-[0.15em] text-white/50 mb-2 block font-sans">Type</label>
                    <div className="flex flex-wrap gap-2">
                      {types.map(type => (
                        <button
                          key={type}
                          onClick={() => setSelectedType(type)}
                          data-testid={`type-option-${type}`}
                          className={`px-4 py-2 text-sm font-sans transition-all ${
                            selectedType === type
                              ? 'bg-[#D4AF37] text-black font-medium'
                              : 'border border-white/20 text-white/60 hover:border-white/40'
                          }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {selectedVariant && (
                  <p className="text-white/30 text-xs font-sans">
                    {selectedVariant.stock > 0 ? `${selectedVariant.stock} in stock` : 'Out of stock'}
                  </p>
                )}
                {!selectedVariant && selectedSize && selectedType && (
                  <p className="text-red-400 text-xs font-sans">This combination is unavailable</p>
                )}
              </div>
            ) : (
              <div className="mb-6 border border-white/10 inline-block px-4 py-2">
                <p className="text-white/60 text-sm font-sans">EDP &middot; 30% Oil &middot; Extrait-Level Performance</p>
              </div>
            )}

            {/* EDP Explainer */}
            <div className="mb-6 bg-[#D4AF37]/5 border border-[#D4AF37]/10 p-4" data-testid="edp-explainer">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-sans mb-2">Why EDP with 30% oil concentration?</p>
              <p className="text-white/40 text-xs leading-relaxed font-sans">
                Every JS Fragrance scent is Eau de Parfum (EDP) bottled at <span className="text-white/70">30% oil concentration</span> — Extrait-level strength. That&apos;s why we call it EDP-Extrait: longer longevity, richer projection, deeper dry-down compared to typical 15-20% EDPs. Premium performance, honest pricing.
              </p>
            </div>

            {/* Live stock urgency — visible counter that promotes honest urgency
                without faking numbers. Threshold 10 so it triggers on healthy
                low-stock SKUs, not the perpetually-empty ones. */}
            {(() => {
              const sNum = selectedVariant ? selectedVariant.stock :
                (product.enable_variants && product.variants?.length
                  ? product.variants.reduce((s, v) => s + (v.stock || 0), 0)
                  : product.stock || 0);
              if (sNum > 0 && sNum <= 10) {
                return (
                  <div
                    className="mb-6 flex items-center gap-3 bg-[#F59E0B]/10 border-l-4 border-[#F59E0B] px-4 py-3"
                    data-testid="live-stock-urgency"
                  >
                    <Flame className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-[#F59E0B] text-sm font-sans font-medium">Only {sNum} left at this price</p>
                      <p className="text-white/45 text-xs font-sans">Restocks bump price after this batch sells out.</p>
                    </div>
                  </div>
                );
              }
              return null;
            })()}

            {/* Quantity */}
            <div className="flex items-center gap-4 mb-6" data-testid="quantity-selector">
              <label className="text-xs uppercase tracking-[0.15em] text-white/50 font-sans">Qty</label>
              <div className="flex items-center border border-white/20">
                <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="p-2 text-white/60 hover:text-white transition-colors" data-testid="qty-decrease">
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center text-white text-sm font-sans" data-testid="qty-value">{quantity}</span>
                <button onClick={() => setQuantity(quantity + 1)} className="p-2 text-white/60 hover:text-white transition-colors" data-testid="qty-increase">
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Add to Cart + Wishlist */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleAddToCart}
                disabled={!canAdd}
                data-testid="add-to-cart-button"
                className={`flex-1 sm:flex-none flex items-center justify-center gap-3 px-8 py-3 text-sm uppercase tracking-[0.15em] font-medium transition-colors ${
                  canAdd
                    ? 'bg-[#D4AF37] text-black hover:bg-[#F3E5AB]'
                    : 'bg-white/10 text-white/30 cursor-not-allowed'
                }`}
              >
                <ShoppingBag className="w-4 h-4" />
                {canAdd ? 'Add to Cart' : 'Unavailable'}
              </button>
              <button
                onClick={toggleWishlist}
                data-testid="wishlist-button"
                className={`p-3 border transition-colors ${wishlisted ? 'border-[#D4AF37] text-[#D4AF37]' : 'border-white/20 text-white/40 hover:border-white/40 hover:text-white'}`}
              >
                <Heart className={`w-5 h-5 ${wishlisted ? 'fill-current' : ''}`} />
              </button>
            </div>

            {/* Delivery note */}
            <p className="text-white/30 text-xs mt-4 font-sans">
              Estimated delivery: 3-7 business days within the Philippines
            </p>

            {/* Share buttons — bot-aware share URL gives rich previews on FB/WhatsApp/X */}
            <ShareButtons product={product} />
          </div>
        </div>

        {/* Scent Profile Section */}
        {product.scent_profile && Object.keys(product.scent_profile).length > 0 && (
          <div className="mt-12 border-t border-white/5 pt-10" data-testid="scent-profile-section">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Scent Profile Bars */}
              <div>
                <h3 className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5 font-sans">Scent Profile</h3>
                <div className="space-y-3">
                  {Object.entries(product.scent_profile).map(([note, level]) => (
                    <div key={note} className="flex items-center gap-3">
                      <span className="text-white/50 text-xs font-sans w-14 capitalize">{note}</span>
                      <div className="flex-1 h-1.5 bg-white/5 overflow-hidden">
                        <div
                          className="h-full bg-[#D4AF37] transition-all duration-1000"
                          style={{ width: `${(level / 5) * 100}%` }}
                          data-testid={`scent-bar-${note}`}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Notes — Pyramid (#10) */}
                {product.notes && Object.keys(product.notes).length > 0 && (
                  <div className="mt-6">
                    <FragrancePyramid
                      top={product.notes.top || []}
                      heart={product.notes.middle || product.notes.heart || []}
                      base={product.notes.base || []}
                    />
                  </div>
                )}
              </div>

              {/* Performance */}
              <div>
                <h3 className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5 font-sans">Performance</h3>
                <div className="space-y-4">
                  {product.performance?.longevity_hours && (
                    <div>
                      <p className="text-white/30 text-xs font-sans mb-1">Longevity</p>
                      <p className="text-white text-sm font-sans">{product.performance.longevity_hours}</p>
                    </div>
                  )}
                  {product.performance?.projection && (
                    <div>
                      <p className="text-white/30 text-xs font-sans mb-1">Projection</p>
                      <p className="text-white text-sm font-sans">{product.performance.projection}</p>
                    </div>
                  )}
                  {product.performance?.sillage && (
                    <div>
                      <p className="text-white/30 text-xs font-sans mb-1">Sillage</p>
                      <p className="text-white text-sm font-sans">{product.performance.sillage}</p>
                    </div>
                  )}
                </div>

                {product.inspired_by && !product.is_signature && (
                  <div className="mt-6 p-3 border border-white/5 bg-white/[0.02]">
                    <p className="text-white/30 text-xs font-sans mb-1">Inspired by</p>
                    <p className="text-white text-sm font-sans">{product.inspired_by}</p>
                  </div>
                )}
              </div>

              {/* Occasions & Best For */}
              <div>
                <h3 className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-5 font-sans">Best For</h3>
                {product.occasion_tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-5">
                    {product.occasion_tags.map(tag => (
                      <span key={tag} className="text-xs px-3 py-1.5 border border-white/10 text-white/60 font-sans">{tag}</span>
                    ))}
                  </div>
                )}

                {product.smells_like && (
                  <div className="mb-4">
                    <p className="text-white/30 text-xs font-sans mb-1">Smells Like</p>
                    <p className="text-white/70 text-sm font-sans italic">"{product.smells_like}"</p>
                  </div>
                )}

                {product.best_for && (
                  <div className="p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/10">
                    <p className="text-[#D4AF37] text-sm font-sans">{product.best_for}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Layering Guide */}
      {product.scent_profile && Object.keys(product.scent_profile).length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 border-t border-white/5" data-testid="layering-section">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] font-sans">Layering Guide</h3>
            {!layeringCombos && (
              <button
                onClick={async () => {
                  setLayeringLoading(true);
                  try {
                    const res = await axios.post(`${API}/layering-guide`, { product_id: product.id });
                    setLayeringCombos(res.data.combos);
                  } catch (e) { console.error(e); }
                  setLayeringLoading(false);
                }}
                disabled={layeringLoading}
                className="text-xs px-4 py-1.5 border border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10 transition-colors font-sans disabled:opacity-50"
                data-testid="get-layering-btn"
              >
                {layeringLoading ? 'Finding combos...' : 'Get Combo Suggestions'}
              </button>
            )}
          </div>
          {layeringCombos && layeringCombos.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {layeringCombos.map((combo, idx) => (
                <Link key={combo.id || idx} to={`/product/${combo.id}`} className="flex gap-3 p-3 bg-[#141414] border border-white/5 hover:border-[#D4AF37]/20 transition-colors group" data-testid={`combo-${idx}`}>
                  <div className="w-16 h-16 bg-[#0A0A0A] flex-shrink-0 overflow-hidden">
                    <img src={combo.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-sans group-hover:text-[#D4AF37] transition-colors truncate">{combo.name}</p>
                    <p className="text-white/40 text-xs font-sans mt-0.5 line-clamp-2">{combo.combo_effect}</p>
                    <p className="text-white/30 text-xs font-sans mt-1">{formatPrice(combo.base_price)}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
          {layeringCombos && layeringCombos.length === 0 && (
            <p className="text-white/30 text-sm font-sans">No layering suggestions available for this scent.</p>
          )}
        </section>
      )}

      {/* Customers Also Bought */}
      {product && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <CustomersAlsoBought productId={product.id} />
        </div>
      )}

      {/* Product Reviews */}
      {product && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProductReviews productId={product.id} productName={product.name} />
        </div>
      )}

      {/* JS Signature Picks (perfume only) */}
      {product && !product.is_signature && (product.category || 'Perfume').toLowerCase() === 'perfume' && (
        <SignaturePicks currentProductId={product.id} />
      )}

      {/* Per-category content blocks (helmet spray, car spray, etc.) */}
      {product && (product.category || '').toLowerCase() !== 'perfume' && (
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
          <CategorySections layout={categoryLayout(product.category)} product={product} />
        </section>
      )}

      {/* Frequently Bought Together */}
      <FrequentlyBought productId={id} />

      {/* Recently Viewed */}
      <RecentlyViewed excludeId={id} />

      {/* #12 — Sticky Add-to-Cart bar (mobile only) */}
      {canAdd && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-md border-t border-white/10 px-4 py-3 flex items-center gap-3 shadow-[0_-4px_20px_rgba(0,0,0,0.4)]"
          data-testid="sticky-add-to-cart"
          style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
        >
          <div className="flex-1 min-w-0">
            <p className="text-white text-sm font-sans font-medium truncate">{product.name}</p>
            <p className="text-[#D4AF37] text-base font-sans font-medium">{formatPrice(displayPrice)}</p>
          </div>
          <button
            onClick={handleAddToCart}
            disabled={!canAdd}
            data-testid="sticky-add-to-cart-btn"
            className="btn-shimmer flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.15em] font-sans font-bold hover:bg-[#E5C75A] transition-colors disabled:opacity-40"
          >
            <ShoppingBag className="w-4 h-4" /> Add
          </button>
        </div>
      )}
    </div>
  );
}


// Map a product's category to the matching CategorySections layout key.
function categoryLayout(cat) {
  const c = (cat || '').toLowerCase().trim();
  if (c.includes('helmet')) return 'helmet_spray';
  if (c.includes('car')) return 'car_spray';
  if (c.includes('home') || c.includes('linen')) return 'home_linen';
  if (c.includes('body mist') || c.includes('mist')) return 'body_mist';
  return 'generic';
}

function SignaturePicks({ currentProductId }) {
  const { formatPrice } = useCurrency();
  const [picks, setPicks] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products?signature=true`).then(res => {
      const filtered = res.data.filter(p => p.id !== currentProductId).slice(0, 4);
      setPicks(filtered);
    }).catch(() => {});
  }, [currentProductId]);

  if (picks.length === 0) return null;

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 mb-6" data-testid="signature-picks">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-[1px] bg-[#1C7D3F]/20" />
        <h3 className="text-sm uppercase tracking-[0.2em] text-[#1C7D3F] font-sans font-medium whitespace-nowrap">JS Signature Scents</h3>
        <div className="flex-1 h-[1px] bg-[#1C7D3F]/20" />
      </div>
      <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
        {picks.map(p => (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            className="group block w-[calc(50%-0.375rem)] sm:w-[200px]"
            data-testid={`sig-pick-${p.id}`}
          >
            <div className="aspect-square overflow-hidden border border-white/5 group-hover:border-[#1C7D3F]/40 transition-colors mb-2">
              <LazyImage src={p.image_url} alt={p.name} className="w-full h-full group-hover:scale-105 transition-transform duration-500" />
            </div>
            <p className="text-white text-xs font-sans group-hover:text-[#1C7D3F] transition-colors truncate">{p.name}</p>
            <p className="text-white/40 text-xs font-sans mt-0.5">{formatPrice(p.base_price)}</p>
          </Link>
        ))}
      </div>
    </section>
  );
}
